import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Menu as MenuIcon, Rocket, LogIn, LogOut, User, X, Sparkles, Users, LayoutGrid, ListChecks, Layers, Store, Scale, CircleHelp, Mail } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAppDomainUrl } from '../../utils/constants';
import seoService from '../../services/seo.service';
import axios from '../../utils/axios';
import { API_ENDPOINTS } from '../../utils/constants';
import { isLoopbackHost } from '../../utils/loopbackHost';
import { resolveMarkbaseProductionAppHostname } from '../../utils/markbaseSpaHosts';
import LogoImage from '../Common/LogoImage';
import { PLATFORM_BRANDING_SITE_LOGO, publicHeaderLogoSources } from '../../utils/platformAssets';
import { resolveDocsPortalUrl, shouldRedirectMarketingDocsToPortal } from '../../utils/externalLinks';
import { landingHeaderMenuItems, LANDING_SECTIONS } from '../../utils/landingSections';
import { lockBodyScroll, unlockBodyScroll } from '../../utils/bodyScrollLock';
import LandingAgentLauncher from './AgentLauncher/LandingAgentLauncher';
import { LANDING_AGENT_OPEN_EVENT } from './AgentLauncher/useLandingAgentChat';

// ✅ БЕЗОПАСНОСТЬ: Константы безопасности
const LANDING_MENU_ICONS = {
  Users,
  Sparkles,
  LayoutGrid,
  ListChecks,
  Layers,
  Store,
  Scale,
  CircleHelp,
  Mail,
};

const AUTH_CHECK_INTERVAL = 60000; // Минимум 1 минута между проверками авторизации

// ✅ БЕЗОПАСНОСТЬ: Максимальная защита - никаких логов и консольных сообщений
// Все ошибки обрабатываются молча, без логирования конфиденциальной информации

// ✅ БЕЗОПАСНОСТЬ: Валидация и санитизация данных пользователя с максимальной защитой
const sanitizeUserData = (userData) => {
  try {
    if (!userData || typeof userData !== 'object' || Array.isArray(userData)) {
      return null;
    }

    // ✅ БЕЗОПАСНОСТЬ: Валидация и очистка строковых полей от потенциально опасного контента
    const sanitizeString = (str, maxLength = 255) => {
      if (typeof str !== 'string') return '';
      // Удаляем потенциально опасные символы и конструкции
      const cleaned = str
        .trim()
        .replace(/[<>]/g, '') // Защита от XSS
        .replace(/javascript:/gi, '') // Защита от javascript: протокола
        .replace(/on\w+\s*=/gi, '') // Удаляем обработчики событий (onclick= и т.д.)
        .replace(/&lt;script/gi, '') // Защита от экранированных скриптов
        .replace(/&lt;\/script&gt;/gi, '') // Защита от экранированных скриптов
        .substring(0, maxLength);
      return cleaned;
    };

    // ✅ БЕЗОПАСНОСТЬ: Валидация URL для аватарки (только https/http, без javascript: и других протоколов)
    const sanitizeUrl = (url) => {
      if (!url || typeof url !== 'string') return null;
      const trimmed = url.trim();
      // Разрешаем только http/https URLs
      if (!/^https?:\/\//i.test(trimmed)) {
        return null; // Отклоняем небезопасные протоколы (javascript:, data:, vbscript:, и т.д.)
      }
      // Проверяем что URL не содержит опасные символы и протоколы
      if (/[<>"'`]/.test(trimmed) || /^(javascript|data|vbscript|file):/i.test(trimmed)) {
        return null;
      }
      // ✅ БЕЗОПАСНОСТЬ: Ограничение длины URL
      if (trimmed.length > 2000) {
        return null;
      }
      return trimmed;
    };

    // ✅ БЕЗОПАСНОСТЬ: Валидация ID (только положительные целые числа)
    const sanitizeId = (id) => {
      if (id === null || id === undefined) return null;
      const numId = Number(id);
      if (isNaN(numId) || numId < 0 || !Number.isInteger(numId)) {
        return null;
      }
      return numId;
    };

    // ✅ БЕЗОПАСНОСТЬ: Валидация email с дополнительной проверкой
    const sanitizeEmail = (email) => {
      if (typeof email !== 'string') return '';
      const cleaned = sanitizeString(email, 255);
      // ✅ БЕЗОПАСНОСТЬ: Базовая валидация формата email (безопасная)
      if (cleaned && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
        return cleaned.toLowerCase(); // Нормализуем к нижнему регистру
      }
      return cleaned.substring(0, 255);
    };

    return {
      id: sanitizeId(userData.id),
      name: sanitizeString(userData.name || '', 255),
      email: sanitizeEmail(userData.email || ''),
      avatar_url: sanitizeUrl(userData.avatar_url) || null,
    };
  } catch (_) {
    // ✅ БЕЗОПАСНОСТЬ: Никаких логов ошибок - просто возвращаем null
    return null;
  }
};

/** Аватар компании/пользователя без AntD — безопасный src, fallback на иконку */
function PublicHeaderAvatar({ company, user, size = 'md' }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [company?.avatar_url, user?.avatar_url]);

  const rawSrc = company?.avatar_url
    ? axios.createFileUrl(company.avatar_url)
    : user?.avatar_url
      ? axios.createFileUrl(user.avatar_url)
      : '';
  const cls =
    size === 'lg'
      ? 'public-header__avatar public-header__avatar--lg'
      : 'public-header__avatar';

  if (!rawSrc || broken) {
    return (
      <span className={`${cls} public-header__avatar--fallback`} aria-hidden>
        <User size={size === 'lg' ? 22 : 18} />
      </span>
    );
  }
  return <img src={rawSrc} alt="" className={cls} onError={() => setBroken(true)} />;
}

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [siteLogoUrl, setSiteLogoUrl] = useState(PLATFORM_BRANDING_SITE_LOGO);
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const [isLandingCondensed, setIsLandingCondensed] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  // ✅ БЕЗОПАСНОСТЬ: Ограничение частоты запросов проверки авторизации
  const lastAuthCheckRef = useRef(0);
  const checkingAuthRef = useRef(true);
  const sectionsRef = useRef(null);

  const host =
    typeof window !== 'undefined' ? String(window.location.hostname || '').toLowerCase() : '';
  const isDocsPortal = host === 'docs.markbase.ru';

  const isLanding =
    (location.pathname === '/' || location.pathname === '') && !isDocsPortal;

  useEffect(() => {
    let lastScrollY = window.scrollY;
    const desktopMq = window.matchMedia('(min-width: 993px)');

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      // На лендинге шапка не скрывается: в ней живёт строка AI — только compact-режим
      if (isLanding) {
        setIsHeaderHidden(false);
        setIsLandingCondensed(currentScrollY > 80);
        lastScrollY = currentScrollY;
        return;
      }
      // На планшетах/телефонах шапка всегда видима — hover-раскрытие недоступно
      if (!desktopMq.matches) {
        setIsHeaderHidden(false);
        lastScrollY = currentScrollY;
        return;
      }
      if (currentScrollY > 150 && currentScrollY > lastScrollY && !mobileMenuVisible) {
        setIsHeaderHidden(true);
      } else {
        setIsHeaderHidden(false);
      }
      lastScrollY = currentScrollY;
    };

    const onBreakpointChange = () => {
      if (!desktopMq.matches) setIsHeaderHidden(false);
      handleScroll();
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    desktopMq.addEventListener('change', onBreakpointChange);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      desktopMq.removeEventListener('change', onBreakpointChange);
    };
  }, [mobileMenuVisible, isLanding]);

  // Одновременно два aria-modal недопустимы: открытие чата закрывает мобильное меню
  useEffect(() => {
    const closeMobileMenu = () => {
      setMobileMenuVisible(false);
      setSectionsOpen(false);
    };
    window.addEventListener(LANDING_AGENT_OPEN_EVENT, closeMobileMenu);
    return () => window.removeEventListener(LANDING_AGENT_OPEN_EVENT, closeMobileMenu);
  }, []);

  useEffect(() => {
    if (!sectionsOpen) return undefined;
    const onPointerDown = (event) => {
      if (!sectionsRef.current?.contains(event.target)) setSectionsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSectionsOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [sectionsOpen]);

  const menuItems = useMemo(() => {
    if (isDocsPortal) {
      // Тот же SPA на текущем хосте — только client-side navigate (без полной перезагрузки).
      return [
        { key: '/', label: 'Сайт' },
        { key: '/pricing', label: 'Тарифы' },
        { key: '/docs', label: 'Документация' },
        { key: '/company', label: 'Сведения' },
        { key: '/contact', label: 'Контакты' },
      ];
    }
    if (isLanding) {
      return landingHeaderMenuItems();
    }
    return [
      { key: '/', label: 'Главная' },
      { key: '/pricing', label: 'Тарифы' },
      { key: '/docs', label: 'Документация' },
      { key: '/company', label: 'Сведения' },
      { key: '/contact', label: 'Контакты' },
    ];
  }, [isDocsPortal, isLanding]);

  const mobileMenuItems = useMemo(() => {
    if (!isLanding) return menuItems;
    const sections = LANDING_SECTIONS.map((section) => ({
      key: `#${section.id}`,
      label: section.label,
      icon: section.icon,
    }));
    return [
      ...sections,
      { key: '/docs', label: 'Документация', icon: 'CircleHelp' },
      { key: '/contact', label: 'Контакты', icon: 'Mail' },
    ];
  }, [isLanding, menuItems]);

  const handleNavClick = (item) => {
    if (item.external) {
      window.location.href = item.external;
      setMobileMenuVisible(false);
      return;
    }
    if (item.key === '/docs' && shouldRedirectMarketingDocsToPortal()) {
      window.location.href = resolveDocsPortalUrl('/docs');
      setMobileMenuVisible(false);
      return;
    }
    if (isLanding && item.key.startsWith('#')) {
      const el = document.querySelector(item.key);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuVisible(false);
      setSectionsOpen(false);
      return;
    }
    navigate(item.key);
    setMobileMenuVisible(false);
    setSectionsOpen(false);
  };

  const handleLogoClick = () => {
    if (isDocsPortal) {
      navigate('/docs');
      return;
    }
    navigate('/');
  };

  const handleLogin = () => {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
    const returnParam = currentUrl ? `?return_url=${encodeURIComponent(currentUrl)}` : '';
    window.location.href = getAppDomainUrl(`/login${returnParam}`);
  };

  const handleRegister = () => {
    // ✅ ИСПРАВЛЕНО: Используем утилиту вместо хардкода
    window.location.href = getAppDomainUrl('/register');
  };

  const handleLogout = async () => {
    try {
      // Несколько POST logout (UAM / текущий origin / app-домен) — намеренно best-effort:
      // кросс-домен и сеть могут дать сбой; пользователю не показываем детали (privacy).
      // Источник истины выхода — очистка токенов и reload ниже.
      // 1. UAM: удаление uam_session на зоне auth
      try {
        const uamUrl = (process.env.REACT_APP_UAM_URL || 'https://auth.markbase.ru').replace(/\/+$/, '');
        await fetch(`${uamUrl}/api/uam/v1/logout`, {
          method: 'POST',
          credentials: 'include',
        }).catch(() => undefined);
      } catch (_) { /* намеренно: не блокируем выход */ }

      // 2. Локальный /api/auth/logout
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }).catch(() => undefined);
      } catch (_) { /* намеренно */ }

      // 3. App-поддомен, если origin отличается от текущего
      const logoutUrl = getAppDomainUrl('/api/auth/logout');
      if (logoutUrl && typeof window !== 'undefined') {
        try {
          const currentOrigin = window.location.origin;
          const logoutOrigin = new URL(logoutUrl).origin;
          if (logoutOrigin !== currentOrigin) {
            await fetch(logoutUrl, {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
            }).catch(() => undefined);
          }
        } catch (_) { /* намеренно */ }
      }

      // 4. Очищаем локальные данные
      axios.clearTokens();
      setUser(null);
      setCompany(null);

      // 5. Обновляем страницу
      window.location.reload();
    } catch (error) {
      // В любом случае очищаем данные и обновляем страницу
      axios.clearTokens();
      setUser(null);
      setCompany(null);
      window.location.reload();
    }
  };

  useEffect(() => {
    checkingAuthRef.current = checkingAuth;
  }, [checkingAuth]);

  // ✅ БЕЗОПАСНОСТЬ: Проверка авторизации пользователя на app.markbase.ru через iframe + postMessage
  // ✅ ЛОГИКА: На markbase.ru всегда проверяем авторизацию через app поддомен (токен хранится там в localStorage)
  useEffect(() => {
    let mounted = true;
    let authCheckIframe = null;
    let messageHandler = null;

    const checkUserAuth = async () => {
      // ✅ БЕЗОПАСНОСТЬ: Ограничение частоты запросов (rate limiting на клиенте)
      const now = Date.now();
      if (now - lastAuthCheckRef.current < AUTH_CHECK_INTERVAL && lastAuthCheckRef.current > 0) {
        // Слишком частый запрос - пропускаем, но НЕ устанавливаем checkingAuth в false
        // если это не первая проверка
        if (mounted && lastAuthCheckRef.current === 0) {
          setCheckingAuth(false);
        }
        return;
      }

      // ✅ При первой проверке устанавливаем checkingAuth в true
      if (mounted && lastAuthCheckRef.current === 0) {
        setCheckingAuth(true);
      }
      lastAuthCheckRef.current = now;

      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

      // ✅ Если мы на app поддомене - используем каноническую cookie-сессию через /me
      if (hostname.startsWith('app.') || isLoopbackHost(hostname)) {
        try {
          const response = await axios.get(API_ENDPOINTS.ME).catch(() => null);
          if (mounted && response?.data?.success) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Local auth check successful on app domain');
            }
            processUserData(response.data);
          } else if (mounted) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ Local auth check failed or no response');
            }
            setUser(null);
            setCompany(null);
          }
        } catch (_) {
          if (mounted) {
            setUser(null);
            setCompany(null);
          }
        }
        if (mounted) {
          setCheckingAuth(false);
        }
        return;
      }

      // ✅ На публичном домене (markbase.ru) сначала пробуем cookie-based auth через тот же домен:
      // современные браузеры могут изолировать localStorage в third-party iframe (storage partitioning),
      // поэтому проверка через /auth-check может вернуть "не авторизован", даже если пользователь вошёл на app.*
      try {
        // 1) Пытаемся получить пользователя через /api/auth/me (если backend поставил httpOnly cookie mb_at)
        try {
          const direct = await axios.get(API_ENDPOINTS.ME, { withCredentials: true, noDedup: true }).catch(() => null);
          if (mounted && direct?.data?.success) {
            processUserData(direct.data);
            setCheckingAuth(false);
            return;
          }
        } catch (_) {
          // молча переходим к fallback через iframe
        }

        // ✅ КРИТИЧНО ИСПРАВЛЕНО: Формируем АБСОЛЮТНЫЙ URL для app поддомена
        // На markbase.ru мы ДОЛЖНЫ использовать https://app.markbase.ru/auth-check
        const protocol = window.location.protocol;
        const hostname = window.location.hostname;

        // ✅ Канон app для зоны markbase.ru — всегда app.markbase.ru (в т.ч. с docs.markbase.ru)
        const appHostname = isLoopbackHost(hostname)
          ? hostname
          : (hostname.startsWith('app.') ? hostname : resolveMarkbaseProductionAppHostname(hostname));

        // ✅ КРИТИЧНО: Формируем АБСОЛЮТНЫЙ URL с полным доменом
        const authCheckFullUrl = `${protocol}//${appHostname}/auth-check`;
        const checkUrl = `${authCheckFullUrl}?origin=${encodeURIComponent(window.location.origin)}`;

        // ✅ КРИТИЧНО: Проверяем что URL правильный и содержит app поддомен
        if (!checkUrl ||
            typeof window === 'undefined' ||
            !checkUrl.includes('/auth-check') ||
            !checkUrl.includes('app.') ||
            checkUrl.endsWith('/?') ||
            checkUrl.includes('app.markbase.ru/?')) {
          if (mounted) {
            setUser(null);
            setCompany(null);
            setCheckingAuth(false);
          }
          return;
        }

        // ✅ ИСПРАВЛЕНО: Получаем базовый URL app поддомена для проверки origin
        // Используем тот же appHostname что и для checkUrl
        const appDomainBaseUrl = `${protocol}//${appHostname}`;

        // ✅ БЕЗОПАСНОСТЬ: Используем iframe + postMessage для проверки авторизации
        // Создаем скрытый iframe с app поддоменом, который проверит токен в своем localStorage и вернет результат
        // ✅ ИСПРАВЛЕНО: URL формируется напрямую через getAppDomainUrl('/auth-check')

        return new Promise((resolve) => {
          // Очищаем предыдущий обработчик если есть
          if (messageHandler) {
            window.removeEventListener('message', messageHandler);
          }

          // Создаем обработчик сообщений от iframe
          // ✅ ИСПРАВЛЕНО: Используем appDomainBaseUrl из внешней области видимости
          messageHandler = (event) => {
            // ✅ БЕЗОПАСНОСТЬ: Максимальная валидация origin сообщения
            const expectedOrigin = appDomainBaseUrl;
            try {
              // ✅ БЕЗОПАСНОСТЬ: Строгая проверка origin - должен быть с app поддомена
              if (!event.origin || typeof event.origin !== 'string') {
                return; // Отклоняем сообщения без origin или с невалидным типом
              }

              const eventOriginUrl = new URL(event.origin);
              const expectedOriginUrl = new URL(expectedOrigin);

              // ✅ БЕЗОПАСНОСТЬ: Проверяем hostname и протокол
              if (eventOriginUrl.hostname !== expectedOriginUrl.hostname ||
                  !eventOriginUrl.hostname.startsWith('app.') ||
                  (eventOriginUrl.protocol !== 'https:' && eventOriginUrl.protocol !== 'http:')) {
                return; // Отклоняем сообщения с невалидным доменом или протоколом
              }
            } catch (_) {
              // ✅ БЕЗОПАСНОСТЬ: Если не удалось распарсить URL - отклоняем без логирования
              return;
            }

            // ✅ БЕЗОПАСНОСТЬ: Валидация структуры данных перед использованием
            try {
              const data = event.data;

              // ✅ БЕЗОПАСНОСТЬ: Проверяем что data является объектом и имеет ожидаемую структуру
              if (!data || typeof data !== 'object' || Array.isArray(data)) {
                return; // Отклоняем невалидные данные
              }

              // ✅ БЕЗОПАСНОСТЬ: Проверяем тип сообщения
              if (data.type !== 'AUTH_CHECK_RESPONSE') {
                return; // Отклоняем сообщения с неожиданным типом
              }

              // ✅ БЕЗОПАСНОСТЬ: Проверяем что success - это boolean
              if (typeof data.success !== 'boolean') {
                return; // Отклоняем сообщения с невалидным success
              }

              // Удаляем обработчик и iframe
              window.removeEventListener('message', messageHandler);
              if (authCheckIframe && authCheckIframe.parentNode) {
                authCheckIframe.parentNode.removeChild(authCheckIframe);
              }
              authCheckIframe = null;
              messageHandler = null;

              // ✅ БЕЗОПАСНОСТЬ: Обрабатываем данные только если успешно и данные валидны
              // ✅ Совместимость: старый формат мог присылать `userData`, новый — `data`
              const payload = (data && typeof data === 'object')
                ? (data.data && typeof data.data === 'object' ? data.data : (data.userData && typeof data.userData === 'object' ? data.userData : null))
                : null;

              if (mounted && data.success && payload) {
                processUserData(payload);
              } else if (mounted) {
                setUser(null);
                setCompany(null);
              }
              if (mounted) {
                setCheckingAuth(false);
              }
              resolve();
            } catch (_) {
              // ✅ БЕЗОПАСНОСТЬ: Никаких логов ошибок - просто очищаем и завершаем
              window.removeEventListener('message', messageHandler);
              if (authCheckIframe && authCheckIframe.parentNode) {
                authCheckIframe.parentNode.removeChild(authCheckIframe);
              }
              authCheckIframe = null;
              messageHandler = null;
              if (mounted) {
                setUser(null);
                setCompany(null);
                setCheckingAuth(false);
              }
              resolve();
            }
          };

          window.addEventListener('message', messageHandler);

          // Создаем скрытый iframe
          authCheckIframe = document.createElement('iframe');
          authCheckIframe.style.display = 'none';
          authCheckIframe.style.width = '0';
          authCheckIframe.style.height = '0';
          authCheckIframe.style.border = 'none';

          // ✅ Обработка ошибок загрузки iframe
          authCheckIframe.onerror = () => {
            // ✅ БЕЗОПАСНОСТЬ: Не логируем ошибки в production
            // Удаляем обработчик и iframe
            window.removeEventListener('message', messageHandler);
            if (authCheckIframe && authCheckIframe.parentNode) {
              authCheckIframe.parentNode.removeChild(authCheckIframe);
            }
            authCheckIframe = null;
            messageHandler = null;
            if (mounted) {
              setUser(null);
              setCompany(null);
              setCheckingAuth(false);
            }
            resolve();
          };

          // ✅ КРИТИЧНО: Финальная проверка URL перед установкой в iframe
          // Проверяем что URL содержит /auth-check и app поддомен, и не является корневым путем
          const urlWithoutQuery = checkUrl.split('?')[0];

          // ✅ Проверка что URL правильный
          if (!checkUrl ||
              !checkUrl.includes('/auth-check') ||
              !checkUrl.includes('app.') ||
              urlWithoutQuery.endsWith('/') ||
              urlWithoutQuery === 'https://app.markbase.ru' ||
              urlWithoutQuery === 'http://app.markbase.ru' ||
              urlWithoutQuery.endsWith('app.markbase.ru') ||
              urlWithoutQuery.endsWith('app.markbase.ru/')) {
            // ✅ БЕЗОПАСНОСТЬ: Не логируем в production, но не создаем iframe с неправильным URL
            if (mounted) {
              setUser(null);
              setCompany(null);
              setCheckingAuth(false);
            }
            resolve();
            return;
          }

          // ✅ Обработка успешной загрузки iframe
          authCheckIframe.onload = () => {
            // ✅ Если iframe загрузился, но не ответил в течение таймаута - это нормально
            // Ответ придет через postMessage
          };

          // ✅ КРИТИЧНО: Устанавливаем src только если URL валиден
          // URL должен быть: https://app.markbase.ru/auth-check?origin=...
          authCheckIframe.src = checkUrl;
          document.body.appendChild(authCheckIframe);

          // Таймаут на случай если iframe не ответит
          setTimeout(() => {
            if (authCheckIframe) {
              window.removeEventListener('message', messageHandler);
              if (authCheckIframe.parentNode) {
                authCheckIframe.parentNode.removeChild(authCheckIframe);
              }
              authCheckIframe = null;
              messageHandler = null;
              if (mounted) {
                setUser(null);
                setCompany(null);
                setCheckingAuth(false);
              }
              resolve();
            }
          }, 5000); // 5 секунд таймаут
        });
      } catch (error) {
        // ✅ БЕЗОПАСНОСТЬ: Пользователь не авторизован - это нормально для публичного сайта
        if (mounted) {
          setUser(null);
          setCompany(null);
          setCheckingAuth(false);
        }
      }
    };

    // ✅ Функция для обработки данных пользователя с максимальной защитой
    const processUserData = (responseData) => {
      try {
        // ✅ БЕЗОПАСНОСТЬ: Валидация структуры данных перед обработкой
        if (!responseData || typeof responseData !== 'object') {
          setUser(null);
          setCompany(null);
          return;
        }

        // ✅ ИСПРАВЛЕНО: Правильная обработка структуры ответа от API
        // API возвращает {success: true, data: {user: {...}, company: {...}}}
        // AuthCheckPage отправляет response.data, который уже содержит {success: true, data: {...}}
        let data = null;
        if (responseData.data && typeof responseData.data === 'object') {
          // Если есть responseData.data, значит это полный ответ от API
          data = responseData.data;
        } else if (responseData.user || responseData.company) {
          // Если нет вложенного data, но есть user/company - значит это уже обработанные данные
          data = responseData;
        } else {
          // Данные не в ожидаемом формате
          setUser(null);
          setCompany(null);
          return;
        }

        if (!data || typeof data !== 'object') {
          setUser(null);
          setCompany(null);
          return;
        }

        const rawUserData = data.user;
        const rawCompanyData = data.company;

        if (rawUserData && typeof rawUserData === 'object' && !Array.isArray(rawUserData)) {
          // ✅ БЕЗОПАСНОСТЬ: Санитизация данных перед использованием
          const sanitizedUser = sanitizeUserData(rawUserData);
          if (sanitizedUser && sanitizedUser.id) {
            setUser(sanitizedUser);

            // Сохраняем данные компании с аватаркой
            if (rawCompanyData && typeof rawCompanyData === 'object' && !Array.isArray(rawCompanyData)) {
              const sanitizeCompanyName = (str) => {
                if (typeof str !== 'string') return '';
                return str
                  .trim()
                  .replace(/[<>]/g, '') // Защита от XSS
                  .replace(/javascript:/gi, '') // Защита от javascript: протокола
                  .replace(/on\w+\s*=/gi, '') // Удаляем обработчики событий
                  .substring(0, 255);
              };

              // ✅ БЕЗОПАСНОСТЬ: Валидация URL аватарки компании с дополнительными проверками
              const sanitizeUrl = (url) => {
                if (!url || typeof url !== 'string') return null;
                const trimmed = url.trim();
                // Разрешаем только http/https URLs
                if (!/^https?:\/\//i.test(trimmed)) {
                  return null;
                }
                // Проверяем что URL не содержит опасные символы и протоколы
                if (/[<>"'`]/.test(trimmed) || /^(javascript|data|vbscript):/i.test(trimmed)) {
                  return null;
                }
                // ✅ БЕЗОПАСНОСТЬ: Ограничение длины URL
                if (trimmed.length > 2000) {
                  return null;
                }
                return trimmed;
              };

              // ✅ БЕЗОПАСНОСТЬ: Валидация ID компании (только числа)
              const sanitizeCompanyId = (id) => {
                if (id === null || id === undefined) return null;
                const numId = Number(id);
                if (isNaN(numId) || numId < 0 || !Number.isInteger(numId)) {
                  return null;
                }
                return numId;
              };

              // ✅ БЕЗОПАСНОСТЬ: Безопасное получение nested данных
              const settings = rawCompanyData.settings && typeof rawCompanyData.settings === 'object'
                ? rawCompanyData.settings
                : null;
              const branding = settings?.branding && typeof settings.branding === 'object'
                ? settings.branding
                : null;
              const companyAvatarUrl = branding?.logo_url
                ? sanitizeUrl(branding.logo_url)
                : null;

              setCompany({
                id: sanitizeCompanyId(rawCompanyData.id),
                name: sanitizeCompanyName(rawCompanyData.name || ''),
                avatar_url: companyAvatarUrl
              });
            } else if (rawUserData.company_name && typeof rawUserData.company_name === 'string') {
              // Fallback на company_name из user, если company не пришла
              const sanitizeCompanyName = (str) => {
                if (typeof str !== 'string') return '';
                return str
                  .trim()
                  .replace(/[<>]/g, '') // Защита от XSS
                  .replace(/javascript:/gi, '') // Защита от javascript: протокола
                  .replace(/on\w+\s*=/gi, '') // Удаляем обработчики событий
                  .substring(0, 255);
              };
              setCompany({
                name: sanitizeCompanyName(rawUserData.company_name)
              });
            } else {
              setCompany(null);
            }
          } else {
            // Данные не прошли валидацию - очищаем
            setUser(null);
            setCompany(null);
          }
        } else {
          setUser(null);
          setCompany(null);
        }
      } catch (_) {
        // ✅ БЕЗОПАСНОСТЬ: Никаких логов ошибок - просто очищаем данные
        setUser(null);
        setCompany(null);
      }
    };

    checkUserAuth();

    // ✅ Дополнительная проверка при фокусе окна (если пользователь вернулся на вкладку)
    const handleFocus = () => {
      if (mounted && !checkingAuthRef.current) {
        // Проверяем авторизацию снова при возврате на вкладку (через 2 секунды после фокуса)
        setTimeout(() => {
          if (mounted) {
            checkUserAuth();
          }
        }, 2000);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      // Очищаем обработчик и iframe при размонтировании
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
      }
      if (authCheckIframe && authCheckIframe.parentNode) {
        authCheckIframe.parentNode.removeChild(authCheckIframe);
      }
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const isSafeBrandingUrl = (url) => {
      if (!url || typeof url !== 'string') return false;
      // Разрешаем только абсолютные http(s) и относительные пути (на нашем домене)
      return /^https?:\/\//i.test(url) || url.startsWith('/');
    };
    (async () => {
      try {
        // ✅ БЕЗОПАСНОСТЬ: Используем публичный эндпоинт мета/брендинга, не требующий авторизации
        // ✅ ОПТИМИЗАЦИЯ: Используем кэшированный запрос (кэш 5 минут, предотвращает массовые запросы)
        const res = await seoService.getMetaTags('landing');
        if (mounted && res?.success) {
          const meta = res.data || {};
          const branding = meta.branding || {};
          // ✅ БЕЗОПАСНОСТЬ: Валидация URL перед установкой
          if (isSafeBrandingUrl(branding.favicon_url)) {
            seoService.setFavicon(branding.favicon_url);
          }
          if (isSafeBrandingUrl(branding.site_logo_url)) {
            setSiteLogoUrl(branding.site_logo_url);
          }
        }
      } catch (_) {
        // ✅ БЕЗОПАСНОСТЬ: Не логируем ошибки (могут содержать конфиденциальную информацию)
      }
    })();
    return () => { mounted = false; };
  }, []);

  const getSelectedKeys = () => {
    if (isLanding) return [];
    if (location.pathname.startsWith('/docs')) return ['/docs'];
    if (location.pathname.startsWith('/help')) return ['/docs'];
    return [location.pathname === '/' ? '/' : location.pathname];
  };

  const isNavKeyActive = (item) => {
    if (item.external) return false;
    return getSelectedKeys().includes(item.key);
  };

  useEffect(() => {
    if (!mobileMenuVisible) return undefined;
    lockBodyScroll();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileMenuVisible(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      unlockBodyScroll();
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileMenuVisible]);

  return (
    <>
      {/* Зона наведения + кнопка «Меню» при скрытой шапке (не на лендинге: там condensed) */}
      {!isLanding ? (
        <div
          className="public-header__reveal-zone"
          onMouseEnter={() => setIsHeaderHidden(false)}
          aria-hidden
        />
      ) : null}
      {!isLanding && isHeaderHidden && !mobileMenuVisible ? (
        <button
          type="button"
          className="public-header__reveal-trigger public-header__reveal-trigger--desktop-only"
          onMouseEnter={() => setIsHeaderHidden(false)}
          onClick={() => setIsHeaderHidden(false)}
          aria-label="Показать меню"
        >
          <MenuIcon size={16} aria-hidden />
          <span>Меню</span>
        </button>
      ) : null}
      <header
        className={[
          'public-header',
          isLanding ? 'public-header--landing' : '',
          isLanding && isLandingCondensed ? 'public-header--landing-condensed' : '',
          isDocsPortal ? 'public-header--docs-portal' : '',
          !isLanding && isHeaderHidden ? 'public-header--hidden' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
      {/* ✅ SVG градиент для логотипа (скрытый) */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#6366f1', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
      </svg>

      <div className="header-container">
        <div className="header-mobile-menu">
          <button
            type="button"
            className={[
              'public-header__menu-toggle',
              isLanding ? 'public-header__menu-toggle--landing' : '',
              mobileMenuVisible ? 'public-header__menu-toggle--open' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-expanded={mobileMenuVisible}
            aria-controls="public-header-mobile-panel"
            aria-label={mobileMenuVisible ? 'Закрыть меню' : 'Открыть меню'}
            onClick={() => setMobileMenuVisible((v) => !v)}
          >
            {mobileMenuVisible ? (
              <X size={22} aria-hidden />
            ) : (
              <MenuIcon size={22} aria-hidden />
            )}
          </button>
        </div>

        <div className="header-logo" onClick={handleLogoClick}>
          {siteLogoUrl ? (
            <>
              {/* ✅ БЕЗОПАСНОСТЬ: Валидация URL логотипа */}
              {siteLogoUrl && (/^https?:\/\//i.test(siteLogoUrl) || siteLogoUrl.startsWith('/')) ? (
                <div className="header-logo-container">
                  <LogoImage
                    sources={publicHeaderLogoSources(siteLogoUrl)}
                    alt="Маркбэйс"
                    mode="image"
                    height={isLanding ? 48 : 36}
                    maxWidth={isLanding ? 188 : 140}
                    style={{ marginRight: 0 }}
                  />
                </div>
              ) : null}
              {!isDocsPortal && !isLanding ? (
                <>
                  <span className="header-logo-divider" />
                  <span className="header-logo-tagline">
                    <span>ЕДИНОЕ</span>
                    <span>УПРАВЛЕНИЕ</span>
                    <span>ТОРГОВЛЕЙ</span>
                  </span>
                </>
              ) : null}
            </>
          ) : (
            <span className="logo-text">Маркбэйс</span>
          )}
          {isDocsPortal ? (
            <span className="public-header__portal-badge">Документация</span>
          ) : null}
        </div>

        {/* Desktop navigation — без AntD Menu */}
        <div className="header-menu-desktop">
          <nav className="header-nav-desktop" aria-label="Основная навигация">
            <ul className={`header-nav-list${isLanding ? ' header-nav-list--landing-anchors' : ''}`}>
              {menuItems.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    className={
                      isNavKeyActive(item)
                        ? 'header-nav-link header-nav-link--active'
                        : 'header-nav-link'
                    }
                    onClick={() => handleNavClick(item)}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Узкий desktop (993–1179): якоря убираются в «Разделы», ширина отдаётся строке AI */}
          {isLanding ? (
            <div className="public-header__sections" ref={sectionsRef}>
              <button
                type="button"
                className="public-header__sections-toggle"
                onClick={() => setSectionsOpen((v) => !v)}
                aria-expanded={sectionsOpen}
                aria-controls="public-header-sections-menu"
              >
                Разделы
                <ChevronDown size={15} aria-hidden />
              </button>
              {sectionsOpen ? (
                <ul className="public-header__sections-menu" id="public-header-sections-menu">
                  {menuItems.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className="public-header__sections-link"
                        onClick={() => handleNavClick(item)}
                      >
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>

        {isLanding ? (
          <div className="public-header__launcher">
            <LandingAgentLauncher />
          </div>
        ) : null}

        <div className="header-end">
          {/* Desktop actions */}
          <div className="header-actions-desktop">
            {checkingAuth ? (
              <div className="public-header__loading-dots public-header__loading-dots--sm" aria-busy="true" aria-live="polite">
                <span className="public-header__loading-dot" />
                <span className="public-header__loading-dot" />
                <span className="public-header__loading-dot" />
              </div>
            ) : user ? (
              <div className="public-header__actions-row">
                <button
                  type="button"
                  className="public-header__user-chip"
                  onClick={() => {
                    const dashboardUrl = getAppDomainUrl('/dashboard');
                    if (dashboardUrl && /^https?:\/\//.test(dashboardUrl)) {
                      window.location.href = dashboardUrl;
                    }
                  }}
                >
                  <PublicHeaderAvatar company={company} user={user} size="md" />
                  <span className="public-header__user-text">
                    <span className="public-header__user-name">
                      {company?.name || user.company_name || 'Моя компания'}
                    </span>
                    <span className="public-header__user-email">{user.email}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="public-header__btn public-header__btn--ghost public-header__btn--with-icon public-header__btn--logout-desktop"
                  onClick={handleLogout}
                  title="Выйти из системы"
                >
                  <LogOut size={16} aria-hidden />
                  <span className="public-header__logout-label-full">Выйти из системы</span>
                  <span className="public-header__logout-label-compact" aria-hidden>
                    Выйти
                  </span>
                </button>
              </div>
            ) : (
              <div className="public-header__auth-group">
                <button
                  type="button"
                  className="public-header__btn public-header__btn--ghost public-header__btn--with-icon"
                  onClick={handleLogin}
                >
                  <LogIn size={16} aria-hidden />
                  Вход
                </button>
                <button
                  type="button"
                  className="public-header__btn public-header__btn--primary public-header__btn--with-icon"
                  onClick={handleRegister}
                >
                  <Rocket size={16} aria-hidden />
                  Регистрация
                </button>
              </div>
            )}
          </div>

          {/* Мобильный лендинг: справа только компактный вход, регистрация — в панели меню */}
          {isLanding && !checkingAuth ? (
            <button
              type="button"
              className="public-header__mobile-login"
              onClick={() => {
                if (!user) {
                  handleLogin();
                  return;
                }
                const dashboardUrl = getAppDomainUrl('/dashboard');
                if (dashboardUrl && /^https?:\/\//.test(dashboardUrl)) {
                  window.location.href = dashboardUrl;
                }
              }}
              aria-label={user ? 'Открыть панель управления' : 'Войти'}
            >
              {user ? <User size={20} aria-hidden /> : <LogIn size={20} aria-hidden />}
            </button>
          ) : null}
        </div>
      </div>

      {mobileMenuVisible ? (
        <>
          <button
            type="button"
            className="public-header-mobile-backdrop"
            aria-label="Закрыть меню"
            onClick={() => setMobileMenuVisible(false)}
          />
          <div
            id="public-header-mobile-panel"
            className={`public-header-mobile-panel${isLanding ? ' public-header-mobile-panel--landing' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="public-header-mobile-title"
          >
            <div className="public-header-mobile-panel__head">
              {isLanding ? (
                <div className="public-header-mobile-panel__brand">
                  <LogoImage
                    sources={publicHeaderLogoSources(siteLogoUrl || PLATFORM_BRANDING_SITE_LOGO)}
                    alt="Маркбэйс"
                    height={48}
                    maxWidth={188}
                    style={{ marginRight: 0 }}
                  />
                  <span className="public-header-mobile-panel__brand-name">Маркбэйс</span>
                </div>
              ) : (
                <h2 id="public-header-mobile-title" className="public-header-mobile-panel__title">
                  Меню
                </h2>
              )}
              {isLanding ? <h2 id="public-header-mobile-title" className="mb-sr-only">Меню</h2> : null}
              <button
                type="button"
                className="public-header-mobile-panel__close"
                onClick={() => setMobileMenuVisible(false)}
                aria-label="Закрыть"
              >
                <X size={22} aria-hidden />
              </button>
            </div>
            <nav className="public-header-mobile-panel__nav" aria-label="Разделы сайта">
              <ul className="public-header-mobile-nav-list">
                {mobileMenuItems.map((item) => {
                  const Icon = LANDING_MENU_ICONS[item.icon] || Sparkles;
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={
                          isNavKeyActive(item)
                            ? 'header-nav-link header-nav-link--mobile header-nav-link--active'
                            : 'header-nav-link header-nav-link--mobile'
                        }
                        onClick={() => handleNavClick(item)}
                      >
                        {isLanding ? (
                          <span className="header-nav-link__icon" aria-hidden>
                            <Icon size={18} strokeWidth={2.1} />
                          </span>
                        ) : null}
                        {item.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
            {isLanding ? (
              <button
                type="button"
                className="public-header-mobile-panel__chat"
                onClick={() => {
                  setMobileMenuVisible(false);
                  window.dispatchEvent(new CustomEvent(LANDING_AGENT_OPEN_EVENT, { detail: { prompt: '' } }));
                }}
              >
                <Sparkles size={18} strokeWidth={2.2} aria-hidden />
                Спросить AI
              </button>
            ) : null}
            <div className="mobile-actions">
              {checkingAuth ? (
                <div className="public-header__loading-dots public-header__loading-dots--lg" aria-busy="true" aria-live="polite">
                  <span className="public-header__loading-dot" />
                  <span className="public-header__loading-dot" />
                  <span className="public-header__loading-dot" />
                </div>
              ) : user ? (
                <div className="public-header-mobile-user">
                  <div className="public-header-mobile-user__row">
                    <PublicHeaderAvatar company={company} user={user} size="lg" />
                    <div className="public-header-mobile-user__text">
                      <span className="public-header-mobile-user__name">
                        {company?.name || user.company_name || 'Моя компания'}
                      </span>
                      <span className="public-header-mobile-user__email">{user.email}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="public-header__btn public-header__btn--ghost public-header__btn--block"
                    onClick={() => {
                      const dashboardUrl = getAppDomainUrl('/dashboard');
                      if (dashboardUrl && /^https?:\/\//.test(dashboardUrl)) {
                        window.location.href = dashboardUrl;
                      }
                    }}
                  >
                    Панель управления
                  </button>
                  <button
                    type="button"
                    className="public-header__btn public-header__btn--danger public-header__btn--block public-header__btn--with-icon"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} aria-hidden />
                    Выйти
                  </button>
                </div>
              ) : (
                <>
                  <button type="button" className="public-header__btn public-header__btn--ghost public-header__btn--block" onClick={handleLogin}>
                    Вход
                  </button>
                  <button
                    type="button"
                    className="public-header__btn public-header__btn--primary public-header__btn--block"
                    onClick={handleRegister}
                  >
                    Регистрация
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      ) : null}
    </header>
    </>
  );
};

export default PublicHeader;

